import type { ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useBugs } from "@/api/bugs";
import { useTaskList } from "@/api/work";
import type { BugSummary } from "@/api/types/BugSummary";
import type { TaskSummary } from "@/api/types/TaskSummary";
import type { UserRef } from "@/api/types/UserRef";
import { Icon, type IconName } from "@/components/Icon";
import { CountsLegend, ProgressBar } from "@/components/Status";
import { Avatar, BotTag, cx, LoadError, PageSpinner } from "@/components/ui";
import { RichText } from "@/components/RichText";
import { minutesToDue } from "@/lib/bugs";
import { relativeTime } from "@/lib/format";
import { isOpen, taskUrl } from "@/lib/work";
import { BugRow } from "../bugs/BugRow";
import { TaskRow } from "../tasks/TaskRow";
import { useProjectContext } from "./ProjectLayout";

const SHOW = 6;
const WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * The project's home: what needs attention now (overdue and major bugs, tasks waiting longest), who is waiting on
 * whom, and the last week's test rounds. Built from the open bug and task lists, so it's always current.
 */
export function ProjectOverview() {
  const { t } = useTranslation();
  const project = useProjectContext();
  const bugs = useBugs(project.key, { open: true });
  const tasks = useTaskList(project.key, { category: ["not_started", "active", "done"], sort: "updated", limit: 1000 });

  if (bugs.isPending || tasks.isPending) return <PageSpinner />;
  if (bugs.isError || tasks.isError) {
    return <LoadError onRetry={() => void Promise.all([bugs.refetch(), tasks.refetch()])} />;
  }

  const now = Date.now();
  const openBugs = bugs.data;
  const overdue = openBugs.filter((b) => (minutesToDue(b.mitigation_due_at, now) ?? 1) < 0);
  const major = openBugs.filter((b) => b.severity === "s1" || b.severity === "s2");
  // Most urgent first: overdue, then severity, then the oldest.
  const urgentBugs = [...openBugs].sort(
    (a, b) =>
      Number((minutesToDue(b.mitigation_due_at, now) ?? 1) < 0) - Number((minutesToDue(a.mitigation_due_at, now) ?? 1) < 0) ||
      a.severity.localeCompare(b.severity) ||
      a.detected_at.localeCompare(b.detected_at),
  );

  const open = tasks.data.filter((x) => isOpen(x.status.category));
  const toTest = open.filter((x) => x.test_stage === "ready_for_test" || x.test_stage === "ready_for_retest" || x.test_stage === "in_testing");
  const toFix = open.filter((x) => x.test_stage === "changes_requested");
  const late = open.filter((x) => x.is_overdue);
  // Tasks someone should be acting on: overdue first, then the longest untouched.
  const waitingTasks = [...new Set([...late, ...toTest, ...toFix])].sort(
    (a, b) => Number(b.is_overdue) - Number(a.is_overdue) || a.updated_at.localeCompare(b.updated_at),
  );

  const rounds = tasks.data
    .flatMap((task) => (task.latest_round?.finished_at ? [{ task, round: task.latest_round }] : []))
    .filter(({ round }) => now - new Date(round.finished_at!).getTime() < WEEK)
    .sort((a, b) => b.round.finished_at!.localeCompare(a.round.finished_at!));

  const bugsUrl = `/p/${project.key}/bugs`;
  const tasksUrl = `/p/${project.key}`;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
        <Tile
          to={`${bugsUrl}?severity=s1&severity=s2`}
          icon="bug"
          label={t("overview.majorBugs")}
          value={major.length}
          hint={t("overview.openBugs", { count: openBugs.length })}
          alert={major.length > 0}
        />
        <Tile to={bugsUrl} icon="clock" label={t("overview.overdueBugs")} value={overdue.length} alert={overdue.length > 0} />
        <Tile to={tasksUrl} icon="list" label={t("overview.openTasks")} value={open.length} />
        <Tile
          to={`${tasksUrl}?due=overdue`}
          icon="calendar"
          label={t("overview.overdueTasks")}
          value={late.length}
          alert={late.length > 0}
        />
        <Tile to={tasksUrl} icon="test" label={t("overview.toTest")} value={toTest.length} />
        <Tile to={tasksUrl} icon="stale" label={t("overview.toFix")} value={toFix.length} />
      </div>

      {/* Full width, like the bug and task lists these rows come from. */}
      <div className="flex flex-col gap-5">
        <Section title={t("overview.bugsTitle")} more={{ to: bugsUrl, label: t("overview.allBugs") }}>
          {urgentBugs.length === 0 ? (
            <Empty>{t("overview.noOpenBugs")}</Empty>
          ) : (
            <div className="divide-y divide-border">
              {urgentBugs.slice(0, SHOW).map((b) => (
                <BugRow key={b.id} bug={b} />
              ))}
            </div>
          )}
        </Section>
        <Section title={t("overview.tasksTitle")} more={{ to: tasksUrl, label: t("overview.allTasks") }}>
          {waitingTasks.length === 0 ? (
            <Empty>{t("overview.nothingWaiting")}</Empty>
          ) : (
            <div className="divide-y divide-border">
              {waitingTasks.slice(0, SHOW).map((x) => (
                <TaskRow key={x.id} task={x} />
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Section title={t("overview.peopleTitle")}>
          <People tasks={open} bugs={openBugs} />
        </Section>
        <Section title={t("overview.roundsTitle")}>
          {rounds.length === 0 ? (
            <Empty>{t("overview.noRounds")}</Empty>
          ) : (
            <ul className="m-0 flex list-none flex-col divide-y divide-border p-0">
              {rounds.slice(0, SHOW).map(({ task, round }) => (
                <li key={`${task.id}-${round.number}`} className="flex flex-col gap-1.5 px-4 py-3">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <Link to={taskUrl(task)} className="font-mono font-semibold">
                      {task.key}
                    </Link>
                    <span className="min-w-0 flex-1 truncate">{task.title}</span>
                    <span className="text-xs text-muted">
                      {t("tasks.roundN", { n: round.number })} · {relativeTime(round.finished_at!)}
                    </span>
                  </div>
                  <ProgressBar counts={round.counts} />
                  <CountsLegend counts={round.counts} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <section className="card p-5">
        <h2 className="m-0 mb-2 label-caps">{t("projects.description")}</h2>
        {project.description ? (
          <RichText text={project.description} className="text-text-2" />
        ) : (
          <p className="m-0 text-text-2">{t("overview.noDescription")}</p>
        )}
      </section>
    </div>
  );
}

function Tile({
  to,
  icon,
  label,
  value,
  hint,
  alert,
}: {
  to: string;
  icon: IconName;
  label: string;
  value: number;
  hint?: string;
  alert?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cx(
        "flex min-w-0 flex-col gap-0.5 rounded-[var(--radius-card)] border bg-surface px-4 py-3 text-text no-underline hover:border-accent hover:text-text hover:no-underline",
        alert ? "border-fail-fg/40" : "border-border",
      )}
    >
      <span className="flex items-center gap-1.5 text-[13px] text-muted">
        <Icon name={icon} size={14} />
        {label}
      </span>
      <span className={cx("text-[22px] leading-tight font-semibold tabular-nums", alert && "text-fail-fg")}>{value}</span>
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </Link>
  );
}

function Section({ title, more, children }: { title: string; more?: { to: string; label: string }; children: ReactNode }) {
  return (
    <section className="card flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="m-0 label-caps">{title}</h2>
        {more && (
          <Link to={more.to} className="text-sm">
            {more.label} →
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

const Empty = ({ children }: { children: ReactNode }) => <p className="m-0 px-4 py-6 text-center text-sm text-muted">{children}</p>;

type Load = { user: UserRef | null; open: number; overdue: number; toTest: number; toFix: number; bugs: number };

/** Per person: open tasks assigned to them (and how many are late), testing waiting on them, bugs they own. */
function People({ tasks, bugs }: { tasks: TaskSummary[]; bugs: BugSummary[] }) {
  const { t } = useTranslation();
  const rows = new Map<string, Load>();
  const row = (user: UserRef | null) => {
    const id = user?.id ?? "";
    if (!rows.has(id)) rows.set(id, { user, open: 0, overdue: 0, toTest: 0, toFix: 0, bugs: 0 });
    return rows.get(id)!;
  };
  for (const task of tasks) {
    const owners = task.assignees.length ? task.assignees : [null];
    for (const u of owners) {
      const r = row(u);
      r.open++;
      if (task.is_overdue) r.overdue++;
      if (task.test_stage === "changes_requested") r.toFix++;
    }
    if (task.test_stage === "ready_for_test" || task.test_stage === "ready_for_retest" || task.test_stage === "in_testing") row(task.tester).toTest++;
  }
  for (const b of bugs) row(b.owner).bugs++;

  const list = [...rows.values()]
    .filter((r) => r.open + r.toTest + r.bugs > 0)
    .sort((a, b) => b.overdue - a.overdue || b.open + b.toTest + b.bugs - (a.open + a.toTest + a.bugs));
  if (list.length === 0) return <Empty>{t("overview.nothingWaiting")}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="table-base">
        <thead>
          <tr>
            <th>{t("overview.person")}</th>
            <th className="text-right">{t("overview.openTasks")}</th>
            <th className="text-right">{t("overview.overdueTasks")}</th>
            <th className="text-right">{t("overview.toTest")}</th>
            <th className="text-right">{t("overview.toFix")}</th>
            <th className="text-right">{t("overview.bugsOwned")}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r.user?.id ?? "nobody"}>
              <td>
                <span className="flex items-center gap-2">
                  {r.user ? <Avatar name={r.user.display_name} bot={r.user.is_bot} size={22} /> : null}
                  <span className={r.user ? undefined : "text-muted"}>{r.user?.display_name ?? t("tasks.unassigned")}</span>
                  {r.user?.is_bot && <BotTag />}
                </span>
              </td>
              <Num value={r.open} />
              <td className={cx("text-right tabular-nums", r.overdue ? "font-semibold text-fail-fg" : "text-muted")}>{r.overdue || "—"}</td>
              <Num value={r.toTest} />
              <Num value={r.toFix} />
              <Num value={r.bugs} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Num = ({ value }: { value: number }) => (
  <td className={cx("text-right tabular-nums", value ? "font-semibold" : "text-muted")}>{value || "—"}</td>
);
